/* eslint-disable @typescript-eslint/no-explicit-any */
import { init } from '@rematch/core';
import createSelectPlugin from '@rematch/select';
import { merge } from 'lodash';
import React from 'react';
import {
  ReactReduxContext,
  useDispatch as coreUseDispatch,
  useSelector as coreUseSelector
} from 'react-redux';

import models from './lib/models';
import createEffectPlugin from './lib/plugins/effectPlugin';
import type { Dispatch, InitConfig, Store } from './lib/types';

export * from './lib/types';
export * from './lib/api/rewardsConnection';
export * from './lib/api/WMTConnection';
export * from './lib/api/bindRequest';
export * from './lib/api/createClient';
export * from './lib/hooks/useClient';
export * from './lib/hooks/useFeature';
export * from './lib/hooks/useSpadeSdk';

export { default as models } from './lib/models';
export * from './lib/models';

const plugins = [createSelectPlugin(), createEffectPlugin()];

export const createStore = <TConfig extends InitConfig<any>>(config: TConfig) => {
  const mergedModels = Object.assign({}, models, config.models);
  const baseConfig = { models: mergedModels, plugins };
  return init(merge(config, baseConfig)) as unknown as Store<TConfig>; // cast to override rematch core types
};

const wrapHook = <THook extends Function, TConfig extends InitConfig<any>>(
  hook: THook,
  config: TConfig
) =>
  ((...args: any[]) => {
    const providedContext = React.useContext(ReactReduxContext);

    if (providedContext) {
      for (const name in config.models) {
        if (!(name in providedContext.store.dispatch)) {
          (providedContext.store as Store).addModel({ ...config.models[name], name });
        }
      }
    }

    return hook(...args);
  }) as unknown as THook;

export const { select, getState } = createStore({ name: 'core-store' });

export const makeUseSelector = (config: InitConfig<any>) => wrapHook(coreUseSelector, config);

export const makeUseDispatch = <TDispatch extends Dispatch>(config: InitConfig<any>) =>
  wrapHook(coreUseDispatch<TDispatch>, config);

export const useDispatch = coreUseDispatch<Dispatch>;
